import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import TopNav from '../components/TopNav';

const Checkout = ({ deviceId }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [step, setStep] = useState(1);
  const [shipping, setShipping] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: ''
  });
  const [email, setEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [processing, setProcessing] = useState(false);

  // Store credit
  const [creditBalance, setCreditBalance] = useState(0);
  const [useCredit, setUseCredit] = useState(true);
  const [creditLoading, setCreditLoading] = useState(false);

  // Promo code
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // { code, discount, percent_off } when applied
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    loadCart();
    // Try to prefill email from logged-in user
    try {
      const stored = localStorage.getItem('loop_user');
      if (stored) {
        const u = JSON.parse(stored);
        if (u && u.email) setEmail(u.email);
      }
    } catch (e) { /* noop */ }
  }, [deviceId]);

  const loadCart = async () => {
    try {
      const data = await api.getCart(deviceId);
      const total = data.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
      setCart({ ...data, total });
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  // Fetch the customer's store credit when we have an email
  const fetchCredit = useCallback(async (mail) => {
    const m = (mail || '').trim().toLowerCase();
    if (!m) {
      setCreditBalance(0);
      return;
    }
    setCreditLoading(true);
    try {
      const res = await api.getProfileCredit(m);
      setCreditBalance(Number(res?.balance ?? 0));
    } catch (e) {
      // Silent — endpoint returns 0 for unknown emails anyway
      setCreditBalance(0);
    } finally {
      setCreditLoading(false);
    }
  }, []);

  // Re-check credit balance whenever the email changes (debounced) and at step 2/3.
  useEffect(() => {
    if (!email) { setCreditBalance(0); return; }
    const t = setTimeout(() => { fetchCredit(email); }, 400);
    return () => clearTimeout(t);
  }, [email, fetchCredit]);

  const subtotal = cart.total;
  const promoDiscount = promo ? Math.min(promo.discount, subtotal) : 0;
  const discountedSubtotal = Math.max(0, +(subtotal - promoDiscount).toFixed(2));
  const appliedCredit = useCredit ? Math.min(creditBalance, discountedSubtotal) : 0;
  const finalTotal = Math.max(0, +(discountedSubtotal - appliedCredit).toFixed(2));
  const fullyCoveredByCredit = finalTotal === 0 && appliedCredit > 0;

  // Re-validate promo if subtotal drops below minimum (e.g., cart changes)
  useEffect(() => {
    if (promo && subtotal < 50) {
      setPromo(null);
      setPromoError('Promo removed: subtotal fell below $50 minimum');
    }
  }, [subtotal, promo]);

  const handleApplyPromo = async () => {
    const code = (promoInput || '').trim();
    if (!code) {
      setPromoError('Enter a promo code');
      return;
    }
    setPromoError('');
    setPromoLoading(true);
    try {
      const result = await api.validatePromo(code, subtotal, email);
      if (result && result.valid) {
        setPromo({
          code: result.code,
          discount: Number(result.discount || 0),
          percent_off: result.percent_off,
          description: result.description,
        });
        setPromoError('');
      } else {
        setPromo(null);
        setPromoError(result?.reason || 'Invalid promo code');
      }
    } catch (e) {
      setPromo(null);
      setPromoError('Could not validate code. Please try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const handlePaymentSelection = (method) => {
    setPaymentMethod(method);
    setStep(3);
  };

  const handlePlaceOrder = async () => {
    setProcessing(true);
    try {
      const orderResult = await api.createOrder(
        deviceId,
        cart.items,
        shipping,
        // If fully covered by credit, skip the gateway entirely.
        fullyCoveredByCredit ? 'credit' : paymentMethod,
        subtotal,
        email,
        appliedCredit,
        promo?.code || '',
      );

      if (!orderResult || !orderResult.success) {
        throw new Error(orderResult?.detail || 'Failed to create order');
      }

      // If credit covers the entire order, skip the payment gateway and go straight to confirmation.
      if (orderResult.fully_covered_by_credit || (orderResult.final_total ?? finalTotal) === 0) {
        navigate(`/order-confirmation?order_id=${encodeURIComponent(orderResult.order_id)}&credit=1`);
        return;
      }

      const remainingTotal = orderResult.final_total ?? finalTotal;

      if (paymentMethod === 'stripe') {
        const stripeResult = await api.createStripeCheckout(
          orderResult.order_id, remainingTotal, {}, window.location.origin
        );
        window.location.href = stripeResult.url;
      } else if (paymentMethod === 'paypal') {
        const paypalResult = await api.createPayPalOrder(orderResult.order_id, remainingTotal);
        if (!paypalResult.approve_url) throw new Error('PayPal approval URL not received');
        window.location.href = paypalResult.approve_url;
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert(`Failed to process order: ${error.message || 'Please try again'}`);
      setProcessing(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3', paddingBottom: '50px' }}>
      <TopNav deviceId={deviceId} />

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '24px', color: '#2C6E49', marginBottom: '30px', textAlign: 'center' }}>[ CHECKOUT ]</h1>

        <div style={{ marginBottom: '30px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <div style={{ padding: '10px 20px', background: step >= 1 ? '#2C6E49' : '#CCC', color: step >= 1 ? '#FEFEE3' : '#666', fontSize: '12px' }}>1. SHIPPING</div>
          <div style={{ padding: '10px 20px', background: step >= 2 ? '#2C6E49' : '#CCC', color: step >= 2 ? '#FEFEE3' : '#666', fontSize: '12px' }}>2. PAYMENT</div>
          <div style={{ padding: '10px 20px', background: step >= 3 ? '#2C6E49' : '#CCC', color: step >= 3 ? '#FEFEE3' : '#666', fontSize: '12px' }}>3. REVIEW</div>
        </div>

        {step === 1 && (
          <form onSubmit={handleShippingSubmit} className="retro-card" style={{ background: '#FFF', padding: '30px' }}>
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>SHIPPING INFORMATION</h2>

            <input required type="email" placeholder="EMAIL" value={email} onChange={(e) => setEmail(e.target.value)} className="retro-input" style={{ width: '100%', marginBottom: '15px' }} />
            <input required placeholder="FULL NAME" value={shipping.name} onChange={(e) => setShipping({...shipping, name: e.target.value})} className="retro-input" style={{ width: '100%', marginBottom: '15px' }} />
            <input required placeholder="ADDRESS" value={shipping.address} onChange={(e) => setShipping({...shipping, address: e.target.value})} className="retro-input" style={{ width: '100%', marginBottom: '15px' }} />
            <input required placeholder="CITY" value={shipping.city} onChange={(e) => setShipping({...shipping, city: e.target.value})} className="retro-input" style={{ width: '100%', marginBottom: '15px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <input required placeholder="STATE" value={shipping.state} onChange={(e) => setShipping({...shipping, state: e.target.value})} className="retro-input" style={{ width: '100%', minWidth: 0 }} />
              <input required placeholder="ZIP CODE" value={shipping.zip_code} onChange={(e) => setShipping({...shipping, zip_code: e.target.value})} className="retro-input" style={{ width: '100%', minWidth: 0 }} />
            </div>
            <input required placeholder="PHONE" value={shipping.phone} onChange={(e) => setShipping({...shipping, phone: e.target.value})} className="retro-input" style={{ width: '100%', marginBottom: '20px' }} />

            {creditBalance > 0 && (
              <div data-testid="checkout-credit-banner" style={{
                background: '#0F0F0F', color: '#4AE54A', padding: '12px 14px',
                border: '3px solid #FFC857', boxShadow: '4px 4px 0 0 #000',
                marginBottom: '20px', fontSize: '12px', letterSpacing: '0.08em',
              }}>
                💰 You have <strong style={{ color: '#FFC857' }}>${creditBalance.toFixed(2)}</strong> store credit.
                It'll auto-apply at the review step.
              </div>
            )}

            <button type="submit" className="retro-btn" style={{ background: '#2C6E49', color: '#FEFEE3', padding: '15px', width: '100%', fontSize: '14px' }}>
              CONTINUE &gt;&gt;&gt;
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="retro-card" style={{ background: '#FFF', padding: '30px' }}>
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>SELECT PAYMENT METHOD</h2>

            {creditBalance > 0 && fullyCoveredByCredit && (
              <div style={{
                background: '#0F0F0F', color: '#4AE54A', padding: '14px',
                border: '3px solid #FFC857', boxShadow: '4px 4px 0 0 #000',
                marginBottom: '20px', fontSize: '12px',
              }}>
                💰 Your store credit covers the full order. You can skip this step — your credit will be applied automatically.
              </div>
            )}

            <button onClick={() => handlePaymentSelection('stripe')} className="retro-btn" style={{ background: '#6772E5', color: '#FFF', padding: '20px', width: '100%', fontSize: '14px', marginBottom: '8px' }}>
              💳 CARD / APPLE PAY / GOOGLE PAY / CASH APP
            </button>
            <p style={{ fontSize: '10px', color: '#888', marginBottom: '8px', letterSpacing: '0.08em', textAlign: 'center' }}>
              + AFFIRM · KLARNA · AFTERPAY (PAY LATER) ON ELIGIBLE ORDERS
            </p>
            <p style={{ fontSize: '10px', color: '#888', marginBottom: '15px', letterSpacing: '0.08em', textAlign: 'center' }}>
              POWERED BY STRIPE — WALLETS APPEAR ON SUPPORTED DEVICES
            </p>

            <button onClick={() => handlePaymentSelection('paypal')} className="retro-btn" style={{ background: '#0070BA', color: '#FFF', padding: '20px', width: '100%', fontSize: '14px' }}>
              PAYPAL
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="retro-card" style={{ background: '#FFF', padding: '30px' }}>
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>REVIEW ORDER</h2>

            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '2px solid #F7F6D8' }}>
              <p style={{ fontSize: '12px', color: '#4C956C', marginBottom: '10px' }}>SHIPPING TO:</p>
              <p style={{ fontSize: '14px', color: '#2C2C2C' }}>{shipping.name}</p>
              <p style={{ fontSize: '12px', color: '#666' }}>{shipping.address}, {shipping.city}, {shipping.state} {shipping.zip_code}</p>
            </div>

            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '2px solid #F7F6D8' }}>
              <p style={{ fontSize: '12px', color: '#4C956C', marginBottom: '10px' }}>PAYMENT METHOD:</p>
              <p style={{ fontSize: '14px', color: '#2C2C2C' }}>
                {fullyCoveredByCredit ? '💰 Store Credit (no card needed)' : (paymentMethod === 'stripe' ? 'Card / Apple Pay / Google Pay / Cash App (Stripe)' : 'PayPal')}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', color: '#4C956C', marginBottom: '10px' }}>ORDER ITEMS:</p>
              {cart.items.map((item) => (
                <div key={item.variant_id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px' }}>{item.name} ({item.color}, {item.size}) x{item.quantity}</span>
                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Promo code */}
            <div data-testid="checkout-promo-section" style={{
              background: '#F7F6D8', padding: '14px 16px',
              border: '3px solid #2C6E49', boxShadow: '4px 4px 0 0 #000',
              marginBottom: '20px',
            }}>
              <p style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', letterSpacing: '0.08em', fontWeight: 'bold' }}>
                PROMO CODE
              </p>
              {promo ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                    <span data-testid="checkout-promo-applied-badge" style={{
                      background: '#4AE54A', color: '#000', padding: '4px 10px',
                      fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.08em',
                      border: '2px solid #000',
                    }}>
                      ✓ {promo.code}
                    </span>
                    <span style={{ fontSize: '12px', color: '#2C2C2C' }}>
                      {promo.percent_off}% OFF applied
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemovePromo}
                    data-testid="checkout-promo-remove"
                    className="retro-btn"
                    style={{ background: '#D62828', color: '#FFF', padding: '8px 14px', fontSize: '11px' }}
                  >
                    REMOVE
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                    <input
                      type="text"
                      placeholder="ENTER CODE"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(); } }}
                      data-testid="checkout-promo-input"
                      className="retro-input"
                      style={{ flex: 1, minWidth: 0, textTransform: 'uppercase' }}
                      disabled={promoLoading}
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoInput.trim()}
                      data-testid="checkout-promo-apply"
                      className="retro-btn"
                      style={{ background: '#2C6E49', color: '#FEFEE3', padding: '0 16px', fontSize: '12px', opacity: (promoLoading || !promoInput.trim()) ? 0.6 : 1, whiteSpace: 'nowrap' }}
                    >
                      {promoLoading ? '...' : 'APPLY'}
                    </button>
                  </div>
                  {promoError && (
                    <p data-testid="checkout-promo-error" style={{ fontSize: '11px', color: '#D62828', marginTop: '8px', letterSpacing: '0.04em' }}>
                      ✕ {promoError}
                    </p>
                  )}
                  <p style={{ fontSize: '10px', color: '#666', marginTop: '8px', letterSpacing: '0.08em' }}>
                    TRY <strong style={{ color: '#2C6E49' }}>LOOPER10</strong> FOR 10% OFF (ORDERS $50+)
                  </p>
                </>
              )}
            </div>

            {/* Store credit toggle */}
            {creditBalance > 0 && (
              <div data-testid="checkout-credit-toggle" style={{
                background: '#0F0F0F', color: '#FEFEE3', padding: '14px 16px',
                border: '3px solid #FFC857', boxShadow: '4px 4px 0 0 #000',
                marginBottom: '20px',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useCredit}
                    onChange={(e) => setUseCredit(e.target.checked)}
                    data-testid="checkout-credit-checkbox"
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', letterSpacing: '0.06em' }}>
                    Apply <strong style={{ color: '#4AE54A' }}>${Math.min(creditBalance, discountedSubtotal).toFixed(2)}</strong> store credit
                  </span>
                </label>
                <div style={{ fontSize: '10px', color: '#999', marginTop: '6px', letterSpacing: '0.1em' }}>
                  Available balance: ${creditBalance.toFixed(2)}
                </div>
              </div>
            )}

            {/* Totals */}
            <div style={{ marginBottom: '30px', paddingTop: '20px', borderTop: '2px solid #000' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>Subtotal</span>
                <span style={{ fontSize: '13px', color: '#2C2C2C', fontFamily: 'IBM Plex Mono, monospace' }}>${subtotal.toFixed(2)}</span>
              </div>
              {promo && promoDiscount > 0 && (
                <div data-testid="checkout-promo-discount-line" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#2C6E49', fontWeight: 'bold' }}>
                    Promo {promo.code} ({promo.percent_off}% off)
                  </span>
                  <span style={{ fontSize: '13px', color: '#2C6E49', fontWeight: 'bold', fontFamily: 'IBM Plex Mono, monospace' }}>-${promoDiscount.toFixed(2)}</span>
                </div>
              )}
              {appliedCredit > 0 && (
                <div data-testid="checkout-applied-credit" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#FFC857', fontWeight: 'bold' }}>Store Credit Applied</span>
                  <span style={{ fontSize: '13px', color: '#FFC857', fontWeight: 'bold', fontFamily: 'IBM Plex Mono, monospace' }}>-${appliedCredit.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #999' }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>TOTAL:</span>
                <span data-testid="checkout-final-total" style={{ fontSize: '18px', fontWeight: 'bold', color: finalTotal === 0 ? '#4AE54A' : '#2C6E49', fontFamily: 'IBM Plex Mono, monospace' }}>
                  ${finalTotal.toFixed(2)}
                </span>
              </div>
            </div>

            <button
              onClick={handlePlaceOrder}
              disabled={processing || creditLoading}
              data-testid="checkout-place-order"
              className="retro-btn"
              style={{ background: '#4AE54A', color: '#000', padding: '20px', width: '100%', fontSize: '14px', opacity: processing ? 0.6 : 1 }}
            >
              {processing
                ? 'PROCESSING...'
                : fullyCoveredByCredit
                  ? '🏆 PLACE ORDER (CREDIT) >>>'
                  : 'PLACE ORDER >>>'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
